import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { getOrderNotificationStatus } from "app/services/api/management";

const T = "pages.dsokOrders.notifications";

// Brevo delivery status → colour. delivered = success, bounce/blocked = error.
function statusColor(status) {
  if (status === "delivered") return "success";
  if (["hardBounces", "softBounces", "blocked", "error", "rejected"].includes(status)) {
    return "error";
  }
  return "default";
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  // DD.MM.RRRR HH:mm:ss (Slovak format per project rule)
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(
    d.getHours()
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function OrderNotificationStatusDialog({
  open,
  onClose,
  externalOrderId,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!externalOrderId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await getOrderNotificationStatus(externalOrderId);
      setDeliveries(Array.isArray(res?.deliveries) ? res.deliveries : []);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [externalOrderId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const channelLabel = (ch) => t(`${T}.channel.${ch}`, ch.toUpperCase());
  const eventLabel = (ev) => t(`${T}.event.${ev}`, ev);
  const statusLabel = (st) => (st ? t(`${T}.event.${st}`, st) : "—");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span style={{ flex: 1 }}>
          {t(`${T}.title`, "Stav notifikácií")} · #{externalOrderId}
        </span>
        <IconButton onClick={load} size="small" title={t(`${T}.refresh`, "Obnoviť")}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error">
            {t(`${T}.error`, "Stav sa nepodarilo načítať.")}
          </Typography>
        ) : deliveries.length === 0 ? (
          <Typography color="text.secondary">
            {t(`${T}.empty`, "Pre túto objednávku neboli odoslané žiadne notifikácie.")}
          </Typography>
        ) : (
          deliveries.map((d, i) => (
            <Box key={d.deliveryId || i} sx={{ mb: i < deliveries.length - 1 ? 2 : 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2">{channelLabel(d.channel)}</Typography>
                {d.recipient && (
                  <Typography variant="body2" color="text.secondary">
                    {d.recipient}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Chip
                  size="small"
                  label={statusLabel(d.deliveryStatus)}
                  color={statusColor(d.deliveryStatus)}
                />
              </Box>
              {d.error && (
                <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                  {d.error}
                </Typography>
              )}
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {(d.events || []).map((e, j) => (
                  <Box component="li" key={j} sx={{ fontSize: 13, color: "text.secondary" }}>
                    <strong>{eventLabel(e.event)}</strong> — {fmt(e.at)}
                    {e.reason ? ` (${e.reason})` : ""}
                  </Box>
                ))}
                {(!d.events || d.events.length === 0) && (
                  <Box component="li" sx={{ fontSize: 13, color: "text.secondary" }}>
                    {t(`${T}.noEvents`, "Zatiaľ bez potvrdenia od operátora.")}
                  </Box>
                )}
              </Box>
              {i < deliveries.length - 1 && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}
